import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle, useMemo } from "react";
import { Box, CircularProgress, Popper, Typography, Tooltip } from '@mui/material';
import { FaExpand, FaCompress, FaHand, FaMagnifyingGlassPlus, FaMagnifyingGlassMinus, FaArrowsRotate, FaChevronDown } from "react-icons/fa6";
import { FaPlayCircle, FaPauseCircle, FaCamera } from "react-icons/fa";
import NaviBox from './NaviBox'

const RESOLUTION_OPTIONS = [
    { code: "UHD", dim: "3840x2160", label: "4K (UHD)" },
    { code: "FHD", dim: "1920x1080", label: "1080p (FHD)" },
    { code: "HD", dim: "1280x720", label: "720p (HD)" },
];

const RESOLUTION_DIMS = {
    "UHD": "3840x2160",
    "4K": "3840x2160",
    "FHD": "1920x1080",
    "1080P": "1920x1080",
    "HD": "1280x720",
    "720P": "1280x720",
    "LOW": "640x360",
    "SD": "640x360",
};




const SCALE_BY = 1.05;
const MAX_SCALE = 6;
const MIN_SCALE = 0.1;
const PANSTEP = 40;

const FixedBox = {
    position: 'absolute',
    top: 0, bottom: 0,
    left: 0, right: 0,
    margin: 'auto',
    zIndex: 1999,
    userSelect: "none",
}
const FlexBox = {
    display: 'flex',
    gap: 2, alignItems: 'center', justifyContent: 'center'
};
const resolveMediaUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("data:") || url.startsWith("blob:")) {
        if (url.startsWith("http://localhost:3000/api/")) {
            const apiBase = (process.env.REACT_APP_URL || "http://127.0.0.1:8000/api/").replace(/\/api\/?$/, "");
            return url.replace("http://localhost:3000", apiBase);
        }
        return url;
    }
    const apiBase = (process.env.REACT_APP_URL || "http://127.0.0.1:8000/api/").replace(/\/api\/?$/, "");
    if (url.startsWith("/")) {
        return `${apiBase}${url}`;
    }
    return `${apiBase}/${url}`;
};

const isStreamUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const lower = url.toLowerCase();
    if (lower.includes(".mp4") || lower.includes(".webm") || lower.includes(".mkv") || lower.includes(".mov")) {
        return false;
    }
    return (
        lower.includes("/video/stream") ||
        lower.includes("/video/stream2") ||
        lower.includes("mjpg") ||
        lower.includes("mjpeg") ||
        lower.includes(".cgi") ||
        lower.includes("image/jpeg") ||
        lower.includes("stream2") ||
        lower.includes("boundary=") ||
        lower.includes("type=fmp4")
    );
};

const VideoViewer = forwardRef(({
    src, type = "video",
    showPrev = true,
    showNext = true,
    icons = {},
    prevPath = "",
    nextPath = "",
    onChange = (data) => { },
    label = "",
    camera = null,
    onPtz = null,
    video_mode = false,
}, ref) => {
    // Only genuine live stream view should be eligible for live camera controls
    const isLiveCamera = Boolean(
        video_mode ||
        camera?.camera_type === "LIVECAMERA" ||
        camera?.camera_type === "LIVETIMELAPSE"
    );

    // Camera truly has pure PTZ capability configured
    const hasPurePtz = Boolean(
        isLiveCamera &&
        (camera?.ptz === true ||
            camera?.ptz === 1 ||
            camera?.ptz === "true" ||
            camera?.has_ptz === true ||
            (Array.isArray(camera?.stream_types) && camera.stream_types.includes("PTZ")) ||
            (Array.isArray(camera?.actions) && camera.actions.some(a => a.action === "ptz" || a.stream_type === "PTZ")))
    );

    // Active by default for live PTZ camera
    const [ptzMode, setPtzMode] = useState(hasPurePtz);
    const [ptzMoving, setPtzMoving] = useState(false);
    const [ptzActionName, setPtzActionName] = useState("");

    // Drag Zoom is active by default for live camera
    const [dragMode, setDragMode] = useState("zoom");
    const [selectionBox, setSelectionBox] = useState(null);

    useEffect(() => {
        setPtzMode(hasPurePtz);
        setDragMode("zoom");
    }, [camera?.id, hasPurePtz]);

    // Live toggle between Zoom In and Zoom Out when holding Alt or Shift during drag
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Alt" || (dragMode === "zoom" && e.key === "Shift")) {
                setSelectionBox((prev) => (prev ? { ...prev, isZoomOut: true } : null));
            }
        };
        const handleKeyUp = (e) => {
            if (e.key === "Alt" || (dragMode === "zoom" && e.key === "Shift")) {
                setSelectionBox((prev) => (prev ? { ...prev, isZoomOut: false } : null));
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        };
    }, [dragMode]);
    useImperativeHandle(ref, () => ({
        getSnapshot: () => {
            const el = mediaRef.current;
            if (!el) return null;

            if (el.tagName === "IMG" && !el.complete) return null;
            if (el.tagName === "VIDEO" && el.readyState < 2) return null;

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const width = el.tagName === "IMG" ? el.naturalWidth : el.videoWidth;
            const height = el.tagName === "IMG" ? el.naturalHeight : el.videoHeight;

            if (!width || !height) return null;

            canvas.width = width;
            canvas.height = height;

            try {
                ctx.drawImage(el, 0, 0, width, height);
                return canvas.toDataURL("image/png");
            } catch (e) {
                console.error("Failed to snapshot media:", e);
                return null;
            }
        }
    }));
    const containerRef = useRef(null);
    const mediaRef = useRef(null);
    const imgRef = useRef(null);
    const videoRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunks = useRef([]);
    const lastTouchDistance = useRef(null);

    const [scale, setScale] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const [last, setLast] = useState({ x: 0, y: 0 });

    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [loader, setLoader] = useState(true);
    const [popup, setPopup] = useState({});
    const [initData, setInitData] = useState({});

    // 1. Instant low-latency MJPEG stream (loads first, zero delay)
    const mjpegSrc = useMemo(() => {
        let candidate =
            camera?.stream_url ||
            (camera?.id ? `/api/video/stream2/?id=${camera.id}&action=stream` : "");
        if (!candidate && isStreamUrl(src)) {
            candidate = src;
        }
        return resolveMediaUrl(candidate);
    }, [camera?.id, camera?.stream_url, src]);

    // 2. Configured H.264 / MP4 Media stream (loads in background)
    const mp4Src = useMemo(() => {
        let candidate =
            camera?.video_url ||
            (src && !isStreamUrl(src) ? src : "");
        if (!candidate || candidate === mjpegSrc) {
            return null;
        }
        return resolveMediaUrl(candidate);
    }, [camera?.video_url, src, mjpegSrc]);

    const [mp4Ready, setMp4Ready] = useState(false);

    const [isFullscreen, setIsFullscreen] = useState(false);
    const [useImgTag, setUseImgTag] = useState(!mp4Src || isStreamUrl(src));
    const [currentSrc, setCurrentSrc] = useState(resolveMediaUrl(src));
    const [selectedRes, setSelectedRes] = useState("");
    const [resMenuOpen, setResMenuOpen] = useState(false);
    const resMenuRef = useRef(null);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (resMenuRef.current && !resMenuRef.current.contains(e.target)) {
                setResMenuOpen(false);
            }
        };
        if (resMenuOpen) {
            document.addEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [resMenuOpen]);

    useEffect(() => {
        setMp4Ready(false);
        setLoader(true);
        const resolved = resolveMediaUrl(src);
        setCurrentSrc(resolved);
    }, [src, camera?.id]);

    // Safety fallback: Ensure loader is dismissed within 2s once stream connects
    useEffect(() => {
        if (!loader) return;
        const timer = setTimeout(() => {
            setLoader(false);
        }, 2200);
        return () => clearTimeout(timer);
    }, [loader, currentSrc]);

    const [showControls, setShowControls] = useState(true);
    const hideTimeoutRef = useRef(null);

    const handleInteraction = () => {
        setShowControls(true);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    };

    useEffect(() => {
        handleInteraction();
        return () => {
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        };
    }, []);

    useEffect(() => {
        const handleChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", handleChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleChange);
        };
    }, []);

    // Workaround for passive event listener warning on wheel events
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const preventDefaultWheel = (e) => e.preventDefault();
        // passive: false is required to allow preventDefault
        container.addEventListener('wheel', preventDefaultWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', preventDefaultWheel);
        };
    }, []);

    // ---------------- FIT TO SCREEN ----------------
    useEffect(() => {
        const media = mediaRef.current;
        const container = containerRef.current;
        if (!media || !container || !currentSrc || (typeof currentSrc === "string" && (currentSrc.includes("undefined") || currentSrc.includes("null")))) return;

        const fit = () => {
            const cw = container.clientWidth;
            const ch = container.clientHeight;
            const media = mediaRef.current;
            const mw = media ? (media.naturalWidth || media.videoWidth || 1920) : 1920;
            const mh = media ? (media.naturalHeight || media.videoHeight || 1080) : 1080;

            if (!mw || !mh) return;

            const fitScale = Math.max(cw / mw, ch / mh);

            const quality = getQuality(mw, mh);
            console.log("STREAM RESOLUTION LOADED:", quality, `${mw}x${mh}`);

            setScale(fitScale);
            setPosition({ x: 0, y: 0 });
            setLoader(false);
            setInitData((prev) => ({ ...prev, fitScale, x: 0, y: 0, quality: prev?.quality || quality }));
        };

        if (type === "video" && !useImgTag) {
            if (media.src !== currentSrc) {
                media.src = currentSrc;
            }
            media.onloadedmetadata = () => {
                setDuration(media.duration || 0);
                fit();
            };
            media.onerror = () => setLoader(false);
            media.onwaiting = () => setLoader(true);
            media.onplaying = () => setLoader(false);
            media.ontimeupdate = () =>
                setCurrentTime(media.currentTime);
        } else {
            if (media.complete && media.naturalWidth > 0) {
                fit();
            } else {
                media.onload = fit;
                media.onerror = () => setLoader(false);
            }
        }
    }, [currentSrc, type, useImgTag]);

    // Helper to safely get natural dimensions of image or video
    const getMediaDims = () => {
        const media = mediaRef.current;
        if (!media) return { mw: 1920, mh: 1080 };
        const mw = media.naturalWidth || media.videoWidth || 1920;
        const mh = media.naturalHeight || media.videoHeight || 1080;
        return { mw, mh };
    };

    // ---------------- CLAMP ------------------------
    const clamp = (x, y, s) => {
        const container = containerRef.current;
        if (!container) return { x, y };

        const cw = container.clientWidth;
        const ch = container.clientHeight;
        const { mw, mh } = getMediaDims();

        const sw = mw * s;
        const sh = mh * s;

        const maxX = Math.max(0, (sw - cw) / 2);
        const maxY = Math.max(0, (sh - ch) / 2);

        return {
            x: Math.min(maxX, Math.max(-maxX, x)),
            y: Math.min(maxY, Math.max(-maxY, y)),
        };
    };

    const clampScale = (s) => {
        return Math.max(MIN_SCALE, Math.min(MAX_SCALE, s));
    };

    // ---------------- ZOOM ----------------
    const handleWheel = (e) => {
        const dir = e.deltaY > 0 ? -1 : 1;

        applyZoom(dir, e.clientX, e.clientY);
    };

    const handleZoom = (type) => {
        const rect = containerRef.current.getBoundingClientRect();

        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const dir = type === "in" ? 1 : -1;

        applyZoom(dir, centerX, centerY);
    };

    const applyZoom = (dir, clientX, clientY) => {
        const rect = containerRef.current.getBoundingClientRect();

        const mx = clientX - rect.left - rect.width / 2;
        const my = clientY - rect.top - rect.height / 2;

        let newScale = dir > 0 ? scale * SCALE_BY : scale / SCALE_BY;
        newScale = clampScale(newScale);

        // Keep point under cursor
        const px = (mx - position.x) / scale;
        const py = (my - position.y) / scale;
        const newX = mx - px * newScale;
        const newY = my - py * newScale;

        const clamped = clamp(newX, newY, newScale);

        setScale(newScale);
        setPosition(clamped);
    };

    // Zoom into a specific client point with target scale (in-place by default to prevent sudden shifting)
    const applyZoomToPoint = (targetScale, clientX, clientY, centerOnPoint = false) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();

        const mx = clientX - rect.left - rect.width / 2;
        const my = clientY - rect.top - rect.height / 2;

        let newScale = clampScale(targetScale);

        let newX, newY;
        if (centerOnPoint) {
            // Target point (clientX, clientY) becomes the center of the zoomed view
            const px = (mx - position.x) / scale;
            const py = (my - position.y) / scale;
            newX = -px * newScale;
            newY = -py * newScale;
        } else {
            // Keep point exactly in-place under cursor without panning or sliding across screen
            const px = (mx - position.x) / scale;
            const py = (my - position.y) / scale;
            newX = mx - px * newScale;
            newY = my - py * newScale;
        }

        const clamped = clamp(newX, newY, newScale);

        setScale(newScale);
        setPosition(clamped);
    };

    // ---------------- DRAG & DRAG-TO-ZOOM ----------------
    const onDown = (e) => {
        if (e.button !== 0) return; // left click only
        handleInteraction();

        const isAlt = e.altKey;
        const isShift = e.shiftKey;
        const isZoomDrag = dragMode === "zoom" || isAlt || isShift;

        if (isZoomDrag) {
            const rect = containerRef.current.getBoundingClientRect();
            const sx = e.clientX - rect.left;
            const sy = e.clientY - rect.top;
            const isZoomOut = isAlt || (dragMode === "zoom" && isShift);
            setSelectionBox({
                startX: sx,
                startY: sy,
                currentX: sx,
                currentY: sy,
                clientStartX: e.clientX,
                clientStartY: e.clientY,
                isZoomOut: isZoomOut,
                active: true,
            });
        } else {
            setDragging(true);
            setLast({ x: e.clientX, y: e.clientY });
        }
    };

    const onMove = (e) => {
        handleInteraction();
        if (selectionBox?.active) {
            const rect = containerRef.current.getBoundingClientRect();
            const cx = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
            const cy = Math.max(0, Math.min(rect.height, e.clientY - rect.top));
            const isZoomOut = e.altKey || (dragMode === "zoom" && e.shiftKey) || selectionBox.isZoomOut;
            setSelectionBox((prev) => (prev ? { ...prev, currentX: cx, currentY: cy, isZoomOut } : null));
        } else if (dragging) {
            const dx = e.clientX - last.x;
            const dy = e.clientY - last.y;

            applyMove(dx, dy); // 🔥 reuse

            setLast({ x: e.clientX, y: e.clientY });
        }
    };

    const onUp = (e) => {
        if (selectionBox?.active) {
            const box = selectionBox;
            setSelectionBox(null);
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const dist = Math.hypot(box.currentX - box.startX, box.currentY - box.startY);

                // Zoom target is the point where the user clicked (startX, startY)!
                const targetClientX = box.clientStartX;
                const targetClientY = box.clientStartY;

                // Only activate zoom when user clicks AND drags (dist >= 15px)
                if (dist >= 15) {
                    const isZoomOut = box.isZoomOut || e?.altKey || (dragMode === "zoom" && e?.shiftKey);
                    const lengthMultiplier = 1 + (dist / 70);

                    // If in PTZ hardware mode on a network live camera, direct call to physical camera PTZ API
                    if (hasPurePtz && ptzMode && onPtz) {
                        const px = Math.round(targetClientX - rect.left);
                        const py = Math.round(targetClientY - rect.top);
                        const imagewidth = Math.round(rect.width);
                        const imageheight = Math.round(rect.height);
                        const z = isZoomOut
                            ? Math.max(25, Math.round(100 / lengthMultiplier))
                            : Math.min(1000, Math.round(lengthMultiplier * 100));

                        setPtzMoving(true);
                        setPtzActionName("areazoom");
                        onPtz("areazoom", { x: px, y: py, z, imagewidth, imageheight });
                        setTimeout(() => {
                            setPtzMoving(false);
                            setPtzActionName("");
                        }, 1500);
                    } else {
                        // Digital mode (Timelapse cameras, non-PTZ cameras, or Digital mode toggle):
                        const minAllowed = initData?.fitScale ? initData.fitScale * 0.9 : MIN_SCALE;
                        const targetScale = isZoomOut
                            ? Math.max(minAllowed, scale / lengthMultiplier)
                            : Math.min(MAX_SCALE, Math.max(scale * 1.25, scale * lengthMultiplier));

                        // Pure digital zoom in-place around the target point
                        applyZoomToPoint(targetScale, targetClientX, targetClientY, false);
                    }
                }
                // Plain single click (dist < 15) is ignored - no accidental zoom
            }
        }
        setDragging(false);
    };

    const handleZoomIn = () => {
        handleInteraction();
        if (hasPurePtz && ptzMode && onPtz) {
            setPtzMoving(true);
            setPtzActionName("zoom-in");
            onPtz("zoom-in");
            setTimeout(() => {
                setPtzMoving(false);
                setPtzActionName("");
            }, 1200);
            return;
        }
        const container = containerRef.current;
        if (container) {
            const rect = container.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const targetScale = Math.min(MAX_SCALE, scale * 1.35);
            applyZoomToPoint(targetScale, centerX, centerY, false);
        }
    };

    const handleZoomOut = () => {
        handleInteraction();
        if (hasPurePtz && ptzMode && onPtz) {
            setPtzMoving(true);
            setPtzActionName("zoom-out");
            onPtz("zoom-out");
            setTimeout(() => {
                setPtzMoving(false);
                setPtzActionName("");
            }, 1200);
            return;
        }
        const container = containerRef.current;
        if (container) {
            const rect = container.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const minAllowed = initData?.fitScale ? initData.fitScale * 0.9 : MIN_SCALE;
            const targetScale = Math.max(minAllowed, scale / 1.35);
            applyZoomToPoint(targetScale, centerX, centerY, false);
        }
    };

    // ---------------- PINCH ZOOM ----------------
    const handleTouchMove = (e) => {
        if (e.touches.length !== 2) return;

        const t1 = e.touches[0];
        const t2 = e.touches[1];

        const dist = Math.hypot(
            t2.clientX - t1.clientX,
            t2.clientY - t1.clientY
        );

        if (!lastTouchDistance.current) {
            lastTouchDistance.current = dist;
            return;
        }

        let newScale = scale * (dist / lastTouchDistance.current);
        newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

        const clamped = clamp(position.x, position.y, newScale);

        setScale(newScale);
        setPosition(clamped);

        lastTouchDistance.current = dist;
    };

    const handleTouchEnd = () => {
        lastTouchDistance.current = null;
    };

    // ---------------- RECORDING ----------------
    const startRecording = () => {
        if (type !== "video") return;

        const stream = mediaRef.current.captureStream();
        const recorder = new MediaRecorder(stream);

        recordedChunks.current = [];

        recorder.ondataavailable = (e) => {
            if (e.data.size > 0)
                recordedChunks.current.push(e.data);
        };

        recorder.onstop = () => {
            const blob = new Blob(recordedChunks.current, {
                type: "video/webm",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "recording.webm";
            a.click();
        };

        recorder.start();
        mediaRecorderRef.current = recorder;
    };

    const stopRecording = () => {
        mediaRecorderRef.current?.stop();
    };

    // ---------------- SCRUB ----------------
    const handleScrub = (e) => {
        const t = Number(e.target.value);
        mediaRef.current.currentTime = t;
        setCurrentTime(t);
    };

    const applyMove = (dx, dy) => {
        const clamped = clamp(
            position.x + dx,
            position.y + dy,
            scale
        );

        setPosition(clamped);
    };

    const handlePanZoom = (direction) => {
        handleInteraction();
        setPtzMoving(true);
        setPtzActionName(direction);
        const autoClear = setTimeout(() => {
            setPtzMoving(false);
            setPtzActionName("");
        }, 1500);

        // Pure Hardware PTZ: Only when camera has PTZ capability and PTZ mode is active
        if (hasPurePtz && ptzMode && onPtz) {
            const res = onPtz(direction);
            if (res && typeof res.then === "function") {
                res.finally(() => {
                    clearTimeout(autoClear);
                    setTimeout(() => {
                        setPtzMoving(false);
                        setPtzActionName("");
                    }, 350);
                });
            }

            if (direction === "reset") {
                setPosition({ x: initData?.x || 0, y: initData?.y || 0 });
                setScale(initData?.fitScale || 1);
            }
            return;
        }

        // Digital client-side Pan & Zoom (for Timelapse cameras, non-PTZ cameras, or Digital mode)
        if (direction === "reset") {
            setPosition({ x: initData?.x || 0, y: initData?.y || 0 });
            setScale(initData?.fitScale || 1);
            return;
        }
        if (direction === "zoom-in" || direction === "zoom-out") {
            return handleZoom(direction === "zoom-in" ? "in" : "out");
        }
        let dx = 0;
        let dy = 0;
        switch (direction) {
            case "up":
                dy = -PANSTEP;
                break;
            case "down":
                dy = PANSTEP;
                break;
            case "left":
                dx = -PANSTEP;
                break;
            case "right":
                dx = PANSTEP;
                break;
            default:
                break;
        }

        applyMove(dx, dy); // 🔥 SAME logic
    };
    const getQuality = (width, height) => {
        if (!width || !height) return "HD";

        // normalize portrait videos
        const w = Math.max(width, height);
        const h = Math.min(width, height);

        if (w >= 3840 && h >= 2160) return "UHD"; // 4K
        if (w >= 1920 && h >= 1080) return "FHD"; // 1080p

        return "HD";
    };

    const toggleFullscreen = () => {
        const el = containerRef.current;

        if (!document.fullscreenElement) {
            if (el.requestFullscreen) el.requestFullscreen();
            else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen(); // Safari
            else if (el.msRequestFullscreen) el.msRequestFullscreen(); // IE
        } else {
            if (document.exitFullscreen) document.exitFullscreen();
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
            else if (document.msExitFullscreen) document.msExitFullscreen();
        }
    };
    const onResolutionChange = (e) => {
        if (type === "image") return;
        const R = e.target.value;
        if (!R) return;

        setLoader(true);
        setSelectedRes(R);

        const targetOption = RESOLUTION_OPTIONS.find(o => o.code === R) || { dim: R, code: R };
        const targetDim = targetOption.dim;

        let url = currentSrc || src || "";
        // Strip previous timestamp
        url = url.replace(/([?&])_t=\d+/, "");

        // Universal resolution param:
        // Axis camera direct URLs require dimensions e.g. 1920x1080.
        // Backend proxy /api/video/stream accepts both 'FHD' and '1920x1080'.
        // So targetDim (e.g. 1920x1080) works universally!
        if (url.includes("resolution=")) {
            url = url.replace(/([?&])resolution=[^&]*/, `$1resolution=${targetDim}`);
        } else {
            const separator = url.includes("?") ? (url.endsWith("?") || url.endsWith("&") ? "" : "&") : "?";
            url = `${url}${separator}resolution=${targetDim}`;
        }

        // Add a timestamp query param to force browser to drop old stream connection and open fresh connection
        const timeSep = url.includes("?") ? (url.endsWith("?") || url.endsWith("&") ? "" : "&") : "?";
        url = `${url}${timeSep}_t=${Date.now()}`;

        setCurrentSrc(resolveMediaUrl(url));
        setInitData((prev) => ({ ...prev, quality: R }));

        const isVideo = mediaRef.current?.tagName === "VIDEO";
        if (isVideo && mediaRef.current) {
            const wasPlaying = !mediaRef.current.paused;
            mediaRef.current.src = url;
            mediaRef.current.load();
            if (wasPlaying) {
                mediaRef.current.play().catch((err) => console.log("Playback error after resolution change:", err));
            } else {
                mediaRef.current.play().catch(() => { });
            }
        }

        setTimeout(() => {
            setLoader(false);
        }, 1200);
    };

    const view = {
        video: (
            <>
                {/* 1. Fast Initial MJPEG Stream - Displays immediately with 0 delay */}
                {mjpegSrc && (
                    <img
                        ref={!mp4Ready ? mediaRef : imgRef}
                        src={mjpegSrc}
                        alt="Live Stream Feed"
                        draggable={false}
                        onLoad={() => {
                            setLoader(false);
                            const media = (!mp4Ready ? mediaRef.current : imgRef.current);
                            const container = containerRef.current;
                            if (media && container && !mp4Ready) {
                                const cw = container.clientWidth;
                                const ch = container.clientHeight;
                                const mw = media.naturalWidth || 1920;
                                const mh = media.naturalHeight || 1080;
                                const fitScale = Math.max(cw / mw, ch / mh);
                                setScale(fitScale);
                                setPosition({ x: 0, y: 0 });
                            }
                        }}
                        onError={() => {
                            if (!mp4Ready) setLoader(false);
                        }}
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                            transformOrigin: "center",
                            transition: dragging ? "none" : "transform 0.3s cubic-bezier(0.2, 0, 0, 1)",
                            opacity: mp4Ready ? 0 : 1,
                            pointerEvents: mp4Ready ? "none" : "all",
                            zIndex: mp4Ready ? 1 : 2,
                        }}
                    />
                )}

                {/* 2. Configured H.264 / MP4 Media Stream - Loads in background, seamlessly takes over once playing */}
                {mp4Src && (
                    <video
                        ref={mp4Ready ? mediaRef : videoRef}
                        src={mp4Src}
                        crossOrigin="anonymous"
                        muted
                        playsInline
                        autoPlay
                        onCanPlay={() => {
                            console.log("H.264/MP4 Media Stream Ready!");
                            setMp4Ready(true);
                            setLoader(false);
                        }}
                        onPlaying={() => {
                            setMp4Ready(true);
                            setLoader(false);
                        }}
                        onError={(e) => {
                            console.warn("H.264/MP4 Stream unavailable, keeping MJPEG stream:", e);
                            setMp4Ready(false);
                        }}
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                            transformOrigin: "center",
                            transition: dragging ? "none" : "transform 0.3s cubic-bezier(0.2, 0, 0, 1)",
                            opacity: mp4Ready ? 1 : 0,
                            pointerEvents: mp4Ready ? "all" : "none",
                            zIndex: mp4Ready ? 2 : 1,
                        }}
                    />
                )}
            </>
        ),
        image: <>
            <img
                ref={mediaRef}
                src={currentSrc || undefined}
                alt=""
                draggable={false}
                onLoad={() => {
                    setLoader(false);
                    const media = mediaRef.current;
                    const container = containerRef.current;
                    if (media && container) {
                        const cw = container.clientWidth;
                        const ch = container.clientHeight;
                        const mw = media.naturalWidth || 1920;
                        const mh = media.naturalHeight || 1080;
                        const fitScale = Math.max(cw / mw, ch / mh);
                        setScale(fitScale);
                        setPosition({ x: 0, y: 0 });
                    }
                }}
                onError={() => setLoader(false)}
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: `translate(-50%, -50%) translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transformOrigin: "center",
                    transition: dragging ? "none" : "transform 0.3s cubic-bezier(0.2, 0, 0, 1)"
                }}
            />
        </>
    }

    const Buttons = {
        video: !useImgTag && <Box sx={{ opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none' }}>
            {!loader && <Box sx={{ ...FixedBox, width: 60, height: 60 }}>
                {mediaRef.current?.paused ? <FaPlayCircle fontSize={48} className="pointer" onClick={() => mediaRef.current?.play()} /> : <FaPauseCircle className="pointer" fontSize={48} onClick={() => mediaRef.current?.pause()} />}
            </Box>}
        </Box>,
        image: <Box sx={{ opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none' }}>
            {showPrev && <Box onMouseEnter={(e) => { setPopup({ target: e.target, prev: true }) }} onMouseLeave={() => { setPopup({}) }}
                onClick={() => { setLoader(true); onChange({ type: "prev" }) }}
                sx={{ ...FixedBox, ...FlexBox, right: 'auto', left: 20, width: 40, height: 64 }}> {icons?.backward} </Box>}
            {showNext && <Box onMouseEnter={(e) => { setPopup({ target: e.target, next: true }) }} onMouseLeave={() => { setPopup({}) }}
                onClick={() => { setLoader(true); onChange({ type: "next" }) }}
                sx={{ ...FixedBox, ...FlexBox, right: 20, left: 'auto', width: 40, height: 64 }}> {icons?.forward} </Box>}
        </Box>
    }
    const takeSnapshot = (ref, filename = "snapshot.png", target = undefined) => {
        const el = ref.current;
        if (!el) return;

        if (el.tagName === "IMG" && !el.complete) return;
        if (el.tagName === "VIDEO" && el.readyState < 2) return;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const width = el.tagName === "IMG" ? el.naturalWidth : el.videoWidth;
        const height = el.tagName === "IMG" ? el.naturalHeight : el.videoHeight;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(el, 0, 0, width, height);
        // if()

        const link = document.createElement("a");
        link.download = filename;
        link.href = canvas.toDataURL("image/png");
        link.click();
    };

    return (
        <>
            <div
                className="icon-filled-black"
                ref={containerRef}
                onWheel={(e) => { handleInteraction(); handleWheel(e); }}
                onMouseDown={(e) => { handleInteraction(); onDown(e); }}
                onMouseMove={(e) => { handleInteraction(); onMove(e); }}
                onMouseUp={onUp}
                onMouseLeave={(e) => { setShowControls(false); onUp(e); }}
                onTouchStart={handleInteraction}
                onTouchMove={(e) => { handleInteraction(); handleTouchMove(e); }}
                onTouchEnd={handleTouchEnd}
                style={{
                    width: "100%",
                    height: "100%",
                    background: "#111",
                    overflow: "hidden",
                    position: "relative",
                    cursor: selectionBox?.active
                        ? (selectionBox.isZoomOut ? "zoom-out" : "crosshair")
                        : (dragMode === "zoom" ? "crosshair" : (dragging ? "grabbing" : "grab")),
                    touchAction: "none",
                    userSelect: 'none'
                }}
                onDoubleClick={() => handlePanZoom("reset")}
            >
                {/* Top control bar: Mode toggle, Snapshot, Resolution, 1.0x Reset, Fullscreen */}
                <Box sx={{
                    ...FixedBox,
                    top: 'auto',
                    bottom: 'auto',
                    left: 'auto',
                    right: 16,
                    top: 16,
                    px: 1.5,
                    py: 1,
                    width: 'auto',
                    height: 'auto',
                    opacity: showControls ? 1 : 0,
                    transition: 'opacity 0.3s',
                    pointerEvents: showControls ? 'auto' : 'none',
                    zIndex: 2001,
                }}>
                    <Box sx={{ ...FlexBox, gap: 1, color: 'white' }}>
                        {/* 1.0x Reset button when zoomed in */}
                        {Math.abs(scale - (initData?.fitScale || 1)) > 0.05 && (
                            <Tooltip title="Reset to 1.0x original fit" arrow>
                                <Box
                                    onClick={() => handlePanZoom("reset")}
                                    sx={{
                                        ...FlexBox,
                                        cursor: 'pointer',
                                        background: '#ffcc33',
                                        color: '#000',
                                        borderRadius: '6px',
                                        px: 1,
                                        height: 26,
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        gap: 0.5,
                                        boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                    }}
                                >
                                    <FaArrowsRotate size={11} />
                                    <span>1.0x</span>
                                </Box>
                            </Tooltip>
                        )}

                        <Box sx={{
                            ...FlexBox,
                            px: 0.8,
                            py: 0.4,
                            background: 'rgba(0,0,0,0.7)',
                            borderRadius: '6px',
                            border: '1px solid rgba(255,255,255,0.2)',
                            height: 26,
                            boxSizing: 'border-box',
                        }} onClick={() => takeSnapshot(mediaRef, `${label}_${Date().toLocaleLowerCase()}.png`)}>
                            <FaCamera className="pointer" color="white" size={15} />
                        </Box>

                        {/* Custom Resolution Dropdown - 100% visible, zero clipping */}
                        {type == "video" && (
                            <Box
                                ref={resMenuRef}
                                sx={{ position: 'relative', display: 'inline-block' }}
                            >
                                <Box
                                    onClick={() => setResMenuOpen(!resMenuOpen)}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        background: 'rgba(20, 20, 20, 0.9)',
                                        backdropFilter: 'blur(8px)',
                                        borderRadius: '6px',
                                        border: '1px solid rgba(255, 204, 51, 0.6)',
                                        px: 1,
                                        height: 26,
                                        boxSizing: 'border-box',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        gap: 0.8,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: '#ffcc33',
                                            boxShadow: '0 0 8px rgba(255, 204, 51, 0.4)',
                                        },
                                    }}
                                >
                                    <span style={{
                                        color: '#ffcc33',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        letterSpacing: '0.3px',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {RESOLUTION_OPTIONS.find(o => o.code === (selectedRes || initData.quality))?.label || "1080p (FHD)"}
                                    </span>
                                    <FaChevronDown
                                        size={8}
                                        style={{
                                            color: '#ffcc33',
                                            transform: resMenuOpen ? 'rotate(180deg)' : 'none',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    />
                                </Box>

                                {resMenuOpen && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 30,
                                            right: 0,
                                            background: 'rgba(20, 20, 20, 0.96)',
                                            backdropFilter: 'blur(12px)',
                                            border: '1px solid rgba(255, 204, 51, 0.5)',
                                            borderRadius: '6px',
                                            boxShadow: '0 6px 20px rgba(0,0,0,0.85)',
                                            zIndex: 3000,
                                            py: 0.5,
                                            minWidth: 105,
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        {RESOLUTION_OPTIONS.map((opt) => {
                                            const isSelected = (selectedRes || initData.quality || "HD") === opt.code;
                                            return (
                                                <Box
                                                    key={opt.code}
                                                    onClick={() => {
                                                        onResolutionChange({ target: { value: opt.code } });
                                                        setResMenuOpen(false);
                                                    }}
                                                    sx={{
                                                        px: 1.5,
                                                        py: 0.6,
                                                        fontSize: '11px',
                                                        fontWeight: isSelected ? 700 : 500,
                                                        color: isSelected ? '#ffcc33' : '#e0e0e0',
                                                        background: isSelected ? 'rgba(255, 204, 51, 0.15)' : 'transparent',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': {
                                                            background: '#ffcc33',
                                                            color: '#000',
                                                            fontWeight: 700,
                                                        },
                                                    }}
                                                >
                                                    <span>{opt.label}</span>
                                                    {isSelected && <span style={{ fontSize: 9, marginLeft: 6 }}>●</span>}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Box className="pointer" sx={{ ...FlexBox, borderRadius: '6px', border: '1px solid rgba(255,255,255,0.3)', minWidth: 26, fontSize: 13, height: 26, background: '#000', px: 0.5, boxSizing: 'border-box' }}>
                            {isFullscreen ? <FaCompress style={{ marginBottom: 2 }} onClick={() => toggleFullscreen()} /> :
                                <FaExpand onClick={() => toggleFullscreen()} />}
                        </Box>
                    </Box>
                </Box>

                {loader && (
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0, 0, 0, 0.65)',
                        backdropFilter: 'blur(4px)',
                        zIndex: 2500,
                        userSelect: 'none',
                        pointerEvents: 'all'
                    }}>
                        <CircularProgress size={46} sx={{ color: "#ffcc33", mb: 1.5 }} thickness={4} />
                        <Typography sx={{ color: "#ffcc33", fontSize: '13px', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                            Loading Media...
                        </Typography>
                    </Box>
                )}

                {/* Drag-to-Zoom Line & Target Reticle */}
                {selectionBox?.active && (() => {
                    const x1 = selectionBox.startX;
                    const y1 = selectionBox.startY;
                    const x2 = selectionBox.currentX;
                    const y2 = selectionBox.currentY;
                    const dist = Math.hypot(x2 - x1, y2 - y1);
                    if (dist < 10) return null; // Don't show visuals on a static click
                    const isZoomOut = !!selectionBox.isZoomOut;
                    const themeColor = isZoomOut ? '#4fc3f7' : '#ffcc33';
                    const estRatio = (1 + dist / 70).toFixed(1);

                    return (
                        <div
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: '100%',
                                height: '100%',
                                pointerEvents: 'none',
                                zIndex: 2005,
                            }}
                        >
                            <svg style={{ width: '100%', height: '100%', position: 'absolute', left: 0, top: 0 }}>
                                <defs>
                                    <filter id="zoom-glow" x="-30%" y="-30%" width="160%" height="160%">
                                        <feGaussianBlur stdDeviation="3" result="blur" />
                                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                    </filter>
                                </defs>
                                {/* Target click center reticle */}
                                <circle cx={x1} cy={y1} r={16} fill={isZoomOut ? "rgba(79, 195, 247, 0.25)" : "rgba(255, 204, 51, 0.25)"} stroke={themeColor} strokeWidth={2} filter="url(#zoom-glow)" />
                                <circle cx={x1} cy={y1} r={3.5} fill={themeColor} />
                                {/* Crosshair lines: horizontal & vertical for (+), horizontal only for (-) */}
                                <line x1={x1 - 10} y1={y1} x2={x1 + 10} y2={y1} stroke={themeColor} strokeWidth={1.5} />
                                {!isZoomOut && (
                                    <line x1={x1} y1={y1 - 10} x2={x1} y2={y1 + 10} stroke={themeColor} strokeWidth={1.5} />
                                )}

                                {/* Drag length line */}
                                <line
                                    x1={x1}
                                    y1={y1}
                                    x2={x2}
                                    y2={y2}
                                    stroke={themeColor}
                                    strokeWidth={3}
                                    strokeDasharray="6,4"
                                    filter="url(#zoom-glow)"
                                />
                                {/* Drag end handle */}
                                <circle cx={x2} cy={y2} r={6} fill={themeColor} stroke="#000" strokeWidth={2} />
                            </svg>

                            {/* Floating Zoom Multiplier Badge */}
                            <div
                                style={{
                                    position: 'absolute',
                                    left: x2 + 14,
                                    top: y2 - 14,
                                    background: 'rgba(0, 0, 0, 0.92)',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    padding: '3px 8px',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.8)',
                                    whiteSpace: 'nowrap',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                <span>{isZoomOut ? `🔍 -${estRatio}x Zoom Out` : `🔍 +${estRatio}x Zoom In`}</span>
                            </div>
                        </div>
                    );
                })()}

                {Buttons[type]}
                {view[type]}

                <Box
                    onMouseEnter={() => setShowControls(true)}
                    sx={{
                        ...FixedBox,
                        top: 'auto',
                        bottom: 24,
                        width: 160,
                        height: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        opacity: showControls ? 1 : 0,
                        transition: 'opacity 0.3s',
                        pointerEvents: showControls ? 'auto' : 'none',
                        zIndex: 2000,
                        overflow: 'visible',
                    }}
                >
                    <NaviBox icons={icons} onChange={handlePanZoom} loading={ptzMoving} activeAction={ptzActionName} />
                </Box>

                {type == "video" && <>
                    <Box sx={{
                        ...FixedBox, top: 'auto', height: 20,
                        paddingLeft: { xs: 1, md: 4 },
                        paddingRight: { xs: 1, md: 4 },
                        opacity: showControls ? 1 : 0, transition: 'opacity 0.3s', pointerEvents: showControls ? 'auto' : 'none'
                    }}>
                        <input
                            className="custom-slider"
                            type="range"
                            min="0"
                            max={duration}
                            step="0.01"
                            value={currentTime}
                            onChange={handleScrub}
                            style={{ height: 10, background: '#ffcc33', width: "100%" }}
                        />
                    </Box>
                </>
                }
            </div>
            <Popper
                open={Boolean(popup?.target)}
                anchorEl={popup?.target}
                placement={popup?.next ? "left-start" : "right-start"}
                modifiers={[{ name: 'offset', options: { offset: [24, 0] } }]}
                sx={{ zIndex: 9999, borderRadius: 2, p: 0, display: { xs: 'none', sm: 'block' } }}
            >
                <Box sx={{ height: 120, borderRadius: 4, border: '1px solid white', overflow: 'hidden' }}>
                    {(showNext && popup?.next) && <img src={nextPath || undefined} height={120} />}
                    {(showPrev && popup?.prev) && <img src={prevPath || undefined} height={120} />}
                </Box>
            </Popper>

            {type === "video" && (
                <div style={{ padding: 10, background: "#222" }}>

                    {/* <button onClick={startRecording}>
                        Start Rec
                    </button>
                    <button onClick={stopRecording}>
                        Stop Rec
                    </button> */}

                    <input
                        type="range"
                        min="0"
                        max={duration}
                        step="0.01"
                        value={currentTime}
                        onChange={handleScrub}
                        style={{ background: '#ffcc33', width: "60%", marginLeft: 20 }}
                    />
                </div>
            )}
        </>
    );
});

export default VideoViewer;